import styleVariables from '!!sass-variable-loader!./../styles/_variables.scss'

import { LazyBrush, Point } from 'lazy-brush'

import { ResizeObserver } from '@juggle/resize-observer';

const LAZY_RADIUS = 0
const BRUSH_RADIUS = 5
const BRUSH_COLOR = styleVariables.colorSponsor2

function midPointBtw(p1, p2) {
  return {
    x: p1.x + (p2.x - p1.x) / 2,
    y: p1.y + (p2.y - p1.y) / 2
  };
}

export default class Scene {
  constructor ({ canvasContainer, sidebar, canvas, picker, slider, button, image, imageThumb }) {
    this.sidebar = document.getElementById(sidebar)
    this.canvasContainer = document.getElementById(canvasContainer)

    this.button = {}
    Object.keys(button).forEach(b => {
      this.button[b] = document.getElementById(button[b])
    })

    document.getElementById(image.id).src = image.src

    this.slider = {}
    Object.keys(slider).forEach(s => {
      this.slider[s] = document.getElementById(slider[s])
    })

    this.picker = {}
    Object.keys(picker).forEach(p => {
      this.picker[p] = document.getElementById(picker[p])
    })

    this.canvas = {}
    this.context = {}
    Object.keys(canvas).forEach(c => {
      const el = document.getElementById(canvas[c])
      this.canvas[c] = el
      this.context[c] = el.getContext('2d')
    })

    this.lazy = new LazyBrush({
      radius: LAZY_RADIUS,
      enabled: true,
      initialPoint: {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2
      }
    })

    this.points = []

    this.mouseHasMoved = true
    this.valuesChanged = true
    this.isDrawing = false
    this.isPressing = false

    if(('ontouchstart' in window) || (navigator.MaxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0)) {
      this.isTouchDevice = true
      this.isColouringModeOn = false
    } else {
      this.isTouchDevice = false
      this.isColouringModeOn = true
    }

    this.points = []

    this.brushRadius = BRUSH_RADIUS
    this.chainLength = LAZY_RADIUS
    this.brushColor = BRUSH_COLOR

    this.dpi = 1
  }

  init () {
    // Listeners for mouse events
    this.canvas.interface.addEventListener('mousedown', this.handlePointerDown.bind(this))
    this.canvas.interface.addEventListener('mouseup', this.handlePointerUp.bind(this))
    this.canvas.interface.addEventListener('mousemove', (e) => this.handlePointerMove(e.clientX - e.target.getBoundingClientRect().left, e.clientY - e.target.getBoundingClientRect().top))
    this.canvas.interface.addEventListener('contextmenu', (e) => this.handleContextMenu(e))

    // Listeners for touch events
    this.canvas.interface.addEventListener('touchstart', (e) => this.handleTouchStart(e))
    this.canvas.interface.addEventListener('touchend', (e) => this.handleTouchEnd(e))
    this.canvas.interface.addEventListener('touchmove', (e) => this.handleTouchMove(e))

    if (this.isTouchDevice) {
      this.button.mode.addEventListener('click', (e) => this.handleButtonMode(e))
    } else {
      document.getElementsByClassName('content__bottom')[0].classList.add('not-touch')
    }
    // Listeners for input events on range sliders
    this.slider.brush.addEventListener('input', (e) => this.handleSliderBrush(e))
    // For IE11
    this.slider.brush.addEventListener('change', (e) => this.handleSliderBrush(e))

    // Set initial value for range sliders
    this.slider.brush.value = BRUSH_RADIUS

    this.picker.brush.addEventListener('input', (e) => this.handlePickerBrush(e))
    this.picker.brush.value = BRUSH_COLOR

    const observeCanvas = new ResizeObserver((entries, observer) => this.handleCanvasResize(entries, observer))
    observeCanvas.observe(this.canvasContainer)

    this.loop()

    window.setTimeout(() => {
      const initX = window.innerWidth / 2
      const initY = window.innerHeight / 2
      this.lazy.update({x: initX - (this.chainLength  / 4), y: initY}, { both: true })
      this.lazy.update({x: initX + (this.chainLength  / 4), y: initY}, { both: false })
      this.mouseHasMoved = true
      this.valuesChanged = true
      this.clearCanvas()
    }, 100)
  }

  handleTouchStart (e) {
    if (this.isColouringModeOn) {
      e.preventDefault();
      const x = e.changedTouches[0].clientX - e.target.getBoundingClientRect().left
      const y = e.changedTouches[0].clientY - e.target.getBoundingClientRect().top
      this.lazy.update({x: x, y: y}, { both: true })
      this.handlePointerDown(e)

      this.mouseHasMoved = true
    }
  }

  handleTouchMove (e) {
    if (this.isColouringModeOn) {
      e.preventDefault()
      this.handlePointerMove(e.changedTouches[0].clientX - e.target.getBoundingClientRect().left, e.changedTouches[0].clientY - e.target.getBoundingClientRect().top)
    }
  }

  handleTouchEnd (e) {
    if (this.isColouringModeOn) {
      this.handlePointerUp(e)
      const brush = this.lazy.getBrushCoordinates()
      this.lazy.update({x: brush.x, y: brush.y}, { both: true })
      this.mouseHasMoved = true
    }
  }

  handleContextMenu (e) {
    e.preventDefault()
    // if (e.button === 2) {
    //   this.clearCanvas()
    // }
  }

  handleButtonMode (e) {
    if (e.target.checked) {
      this.isColouringModeOn = true
      document.documentElement.style.touchAction = "none"
    } else {
      this.isColouringModeOn = false
      document.documentElement.style.touchAction = "auto"
    }
  }

  handleButtonLazy (e) {
    e.preventDefault()
    this.valuesChanged = true
    this.button.lazy.classList.toggle('disabled')

    if (this.lazy.isEnabled()) {
      this.button.lazy.innerHTML = 'Off'
      this.lazy.disable()
    } else {
      this.button.lazy.innerHTML = 'On'
      this.lazy.enable()
    }
  }

  handleCanvasResize (entries, observer) {
    this.dpi = window.devicePixelRatio

    for (var i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const { width, height } = entry.contentRect

      this.setCanvasSize(this.canvas.interface, width, height, 1)
      this.setCanvasSize(this.canvas.drawing, width, height, 1)
      this.setCanvasSize(this.canvas.temp, width, height, 1)

      this.loop({ once: true })
    }
  }

  handleSliderBrush (e) {
    const val = parseInt(e.target.value)
    this.valuesChanged = true
    this.brushRadius = val
  }

  handlePickerBrush (e) {
    const val = e.target.value
    this.valuesChanged = true
    this.brushColor = val
  }

  handleSliderLazy (e) {
    this.valuesChanged = true
    const val = parseInt(e.target.value)
    this.chainLength = val
    this.lazy.setRadius(val)
  }

  setCanvasSize (canvas, width, height, maxDpi = 4) {
    let dpi = this.dpi 

    dpi = Math.min(this.dpi, maxDpi)

    canvas.width = width * dpi
    canvas.height = height * dpi
    canvas.style.width = width 
    canvas.style.height = height
    // canvas.getContext('2d').scale(dpi, dpi)
  }

  handlePointerDown (e) {
    e.preventDefault()
    this.isPressing = true
  }

  handlePointerUp (e) {
    e.preventDefault()
    this.isDrawing = false
    this.isPressing = false
    this.points.length = 0

    const dpi = window.devicePixelRatio
    const width = this.canvas.temp.width 
    const height = this.canvas.temp.height 

    this.context.drawing.drawImage(this.canvas.temp, 0, 0, width, height)
    this.context.temp.clearRect(0, 0, width, height)
  }

  handlePointerMove (x, y) {
    const hasChanged = this.lazy.update({ x: x, y: y})
    const isDisabled = !this.lazy.isEnabled()

    this.context.temp.lineJoin = 'round'
    this.context.temp.lineCap = 'round'
    this.context.temp.strokeStyle = this.brushColor

    if ((this.isPressing && hasChanged && !this.isDrawing) || (isDisabled && this.isPressing)) {
      this.isDrawing = true
      this.points.push(this.lazy.brush.toObject())
    }

    if (this.isDrawing && (this.lazy.brushHasMoved() || isDisabled)) {

      this.context.temp.clearRect(0, 0, this.context.temp.canvas.width, this.context.temp.canvas.height)
      this.context.temp.lineWidth = this.brushRadius * 1.5
      this.points.push(this.lazy.brush.toObject())

      var p1 = this.points[0]
      var p2 = this.points[1]

      this.context.temp.moveTo(p2.x, p2.y)
      this.context.temp.beginPath()

      for (var i = 1, len = this.points.length; i < len; i++) {
        // we pick the point between pi+1 & pi+2 as the
        // end point and p1 as our control point
        var midPoint = midPointBtw(p1, p2)
        this.context.temp.quadraticCurveTo(p1.x, p1.y, midPoint.x, midPoint.y);
        p1 = this.points[i]
        p2 = this.points[i+1];
      }
      // Draw last line as a straight line while
      // we wait for the next point to be able to calculate
      // the bezier control point
      this.context.temp.lineTo(p1.x, p1.y)
      this.context.temp.stroke()
    }

    this.mouseHasMoved = true
  }

  clearCanvas () {
    this.valuesChanged = true
    this.context.drawing.clearRect(0, 0, this.canvas.drawing.width, this.canvas.drawing.height)
    this.context.temp.clearRect(0, 0, this.canvas.temp.width, this.canvas.temp.height)
  }

  loop ({ once = false } = {}) {
    if (this.mouseHasMoved || this.valuesChanged) {
      const pointer = this.lazy.getPointerCoordinates()
      const brush = this.lazy.getBrushCoordinates()

      this.drawInterface(this.context.interface, pointer, brush)
      this.mouseHasMoved = false
      this.valuesChanged = false
    }

    if (!once) {
      window.requestAnimationFrame(() => {
        this.loop()
      })
    }
  }

  drawInterface (ctx, pointer, brush) {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height)

    // Draw brush point
    ctx.beginPath()
    ctx.fillStyle = this.brushColor
    ctx.arc(brush.x, brush.y, this.brushRadius, 0, Math.PI * 2, true)
    ctx.fill()
  }
}
